/**
 * ClientForm — the editable form for a client cadastro.
 *
 * Rendered in TWO places so the user has a single screen experience:
 *   1) Inside the Client Detail view (when editing an existing client) →
 *      replaces the read-only info card so the manager can edit on the
 *      same page that shows photos, documents, notes history and facade.
 *   2) Inside the Client List view (when creating a new client) → table
 *      hides while the form is open.
 *
 * Stateless on its own — all state lives in the parent (`ClientsModule`)
 * and flows in via props.
 */
import {
  Plus, X, Check, User, FileText, Briefcase, DollarSign, Clock,
} from "lucide-react";
import { formatUSD } from "../utils/currency";

const OTHER_SENTINEL = "__other__";

export default function ClientForm({
  editingClient,
  form,
  setForm,
  onSubmit,
  onCancel,
  onCreateTicket,
  otherChecked,
  setOtherChecked,
  catalog,
  staff,
  toggleServiceId,
  updateServicePrice,
}) {
  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm" data-testid="client-form">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="font-semibold text-stone-800">{editingClient ? "Edit Client" : "New Client"}</h3>
        <div className="flex items-center gap-2">
          {editingClient && onCreateTicket && (
            <button
              type="button"
              data-testid="create-new-ticket-form-btn"
              onClick={() => onCreateTicket(editingClient.id)}
              className="inline-flex items-center gap-1.5 bg-forest-600 hover:bg-forest-700 text-white px-3.5 py-2 rounded-lg text-sm font-medium shadow-sm transition-colors"
              title="Open a new Service Order pre-filled with this client's data"
            >
              <Plus className="w-4 h-4" /> Create New Ticket
            </button>
          )}
          <button onClick={onCancel} className="text-stone-400 hover:text-stone-600"><X className="w-5 h-5" /></button>
        </div>
      </div>
      <form onSubmit={onSubmit} className="space-y-4">
        {/* Row 1: Property Type + Name */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Property Type</label>
            <select data-testid="client-type-select" value={form.property_type}
              onChange={(e) => setForm({ ...form, property_type: e.target.value })}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500">
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              {form.property_type === "commercial" ? "Company / Establishment Name *" : "Owner Name *"}
            </label>
            <input data-testid="client-name-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={form.property_type === "commercial" ? "e.g. Candlewood Suites Macon" : "e.g. John Smith"}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500" required />
          </div>
        </div>

        {form.property_type === "commercial" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50/50 rounded-xl border border-blue-100" data-testid="commercial-fields">
            <div>
              <label className="block text-sm font-medium text-blue-800 mb-1 flex items-center gap-1">
                <User className="w-3.5 h-3.5" /> Responsible Contact *
              </label>
              <input data-testid="client-responsible-contact" value={form.responsible_contact}
                onChange={(e) => setForm({ ...form, responsible_contact: e.target.value })}
                placeholder="e.g. Zandra Greene"
                className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-blue-800 mb-1 flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" /> Tax ID / EIN
              </label>
              <input data-testid="client-tax-id" value={form.tax_id}
                onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                placeholder="e.g. 58-1234567"
                className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white" />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Phone *</label>
            <input data-testid="client-phone-input" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Email</label>
            <input data-testid="client-email-input" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Address *</label>
          <input data-testid="client-address-input" value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500" required />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">City *</label>
            <input data-testid="client-city-input" value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">State</label>
            <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500" maxLength={2} />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">ZIP Code</label>
            <input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500" />
          </div>
        </div>

        {/* Internal Assignment */}
        <div className="border-t border-stone-100 pt-4">
          <h4 className="text-sm font-semibold text-stone-700 flex items-center gap-2 mb-3">
            <Briefcase className="w-4 h-4 text-forest-600" /> Internal Assignment
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Account Manager</label>
              <select
                data-testid="client-account-manager-select"
                value={form.account_manager_id || ""}
                onChange={(e) => setForm({ ...form, account_manager_id: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 bg-white"
              >
                <option value="">— Unassigned —</option>
                {staff.managers.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}{m.role === "admin" ? " · Admin" : " · Manager"}</option>
                ))}
              </select>
              <p className="text-[11px] text-stone-400 mt-1">Responsible day-to-day for this account. Limited to Admins and Managers.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Acquired By</label>
              <select
                data-testid="client-acquired-by-select"
                value={form.acquired_by_id || ""}
                onChange={(e) => setForm({ ...form, acquired_by_id: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 bg-white"
              >
                <option value="">— Unknown —</option>
                {staff.all_staff.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.title ? ` · ${s.title}` : ""}</option>
                ))}
              </select>
              <p className="text-[11px] text-stone-400 mt-1">Employee who closed the contract — used for commission and sales history.</p>
            </div>
          </div>
        </div>

        {/* Notes (writes a new note to history on save when editing) */}
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">
            {editingClient ? "Add Note" : "Initial Notes"}
          </label>
          <textarea data-testid="client-notes-input" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
            placeholder={editingClient ? "Type a new note to add to the history below. Saved with date, time, and your name." : "Optional initial note..."}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 resize-none" />

          {editingClient && Array.isArray(editingClient.notes_history) && editingClient.notes_history.length > 0 && (
            <div className="mt-3 border border-stone-200 rounded-lg bg-stone-50/50" data-testid="form-notes-history">
              <div className="px-3 py-2 border-b border-stone-200 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                <Clock className="w-3.5 h-3.5" /> Notes History · {editingClient.notes_history.length}
              </div>
              <ul className="max-h-56 overflow-y-auto divide-y divide-stone-100">
                {[...editingClient.notes_history].reverse().map((n) => (
                  <li key={n.id || `${n.created_at}-${(n.text || "").slice(0, 16)}`} className="px-3 py-2 text-xs">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <span className="font-medium text-stone-700 truncate">{n.created_by || "System"}</span>
                      <span className="text-[10px] text-stone-400 whitespace-nowrap">
                        {n.created_at ? new Date(n.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                      </span>
                    </div>
                    <p className="text-stone-600 whitespace-pre-wrap break-words">{n.text}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Contracted / Requested Services */}
        <div data-testid="contracted-services-section" className="border-t border-stone-100 pt-4">
          <h4 className="text-sm font-semibold text-stone-700 flex items-center gap-2 mb-1">
            <Briefcase className="w-4 h-4 text-forest-600" /> Contracted / Requested Services
          </h4>
          <p className="text-[11px] text-stone-400 mb-3">
            Check everything this client has signed up for. New tickets opened from this profile auto-fill these services.
          </p>
          {catalog.length === 0 ? (
            <p className="text-xs text-stone-400">No services in the catalog yet. Ask an admin to seed the catalog in Admin Center → Service Catalog.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {catalog.map((item) => {
                const checked = (form.service_ids || []).includes(item.id);
                const customPrice = form.service_prices?.[item.id];
                const priceShown = checked ? (customPrice !== undefined ? customPrice : item.default_price) : item.default_price;
                const isOverridden = checked && customPrice !== undefined && Number(customPrice) !== Number(item.default_price);
                return (
                  <div
                    key={item.id}
                    data-testid={`service-row-${item.id}`}
                    className={`px-3 py-2 rounded-lg border transition-colors ${checked ? "border-forest-400 bg-forest-50" : "border-stone-200 hover:bg-stone-50"}`}
                  >
                    <label data-testid={`service-checkbox-${item.id}`} className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={checked} onChange={() => toggleServiceId(item.id)} className="mt-0.5 accent-forest-600" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-stone-800 truncate">{item.name}</p>
                        {!checked && Number(item.default_price) > 0 && (
                          <p className="text-[11px] text-stone-400">{formatUSD(item.default_price)}</p>
                        )}
                      </div>
                    </label>
                    {checked && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-[11px] text-stone-500 font-medium">Price for this client</span>
                        <div className="relative flex-1 max-w-[140px]">
                          <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                          <input
                            type="number" min="0" step="0.01" value={priceShown}
                            onChange={(e) => updateServicePrice(item.id, e.target.value)}
                            data-testid={`service-price-input-${item.id}`}
                            className="w-full pl-7 pr-2 py-1 border border-stone-300 rounded-md text-xs focus:ring-2 focus:ring-forest-500 bg-white"
                          />
                        </div>
                        {isOverridden && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" title={`Catalog default: ${formatUSD(item.default_price)}`}>
                            Override
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Others toggle */}
              <label
                data-testid={`service-checkbox-${OTHER_SENTINEL}`}
                className={`flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${otherChecked ? "border-amber-400 bg-amber-50" : "border-stone-200 hover:bg-stone-50"}`}
              >
                <input type="checkbox" checked={otherChecked} onChange={() => setOtherChecked((v) => !v)} className="mt-0.5 accent-amber-600" />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-stone-800">Others</p>
                  <p className="text-[11px] text-stone-400">Free-text service</p>
                </div>
              </label>
            </div>
          )}
          {otherChecked && (
            <input
              data-testid="services-other-input"
              value={form.services_other}
              onChange={(e) => setForm({ ...form, services_other: e.target.value })}
              placeholder="e.g. Tree removal, Pressure washing"
              className="mt-3 w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 bg-white"
            />
          )}
        </div>

        {/* Submit */}
        <div className="flex gap-3 pt-1">
          <button data-testid="client-submit-btn" type="submit"
            className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors">
            <Check className="w-4 h-4" />{editingClient ? "Update" : "Create"}
          </button>
          <button type="button" onClick={onCancel}
            className="px-5 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors">Cancel</button>
        </div>
      </form>
    </div>
  );
}
